import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  showChevron?: boolean;
}

const variantStyles: Record<Variant, string> = {
  primary: cn(
    'bg-cta-primary text-white border border-brand-300/40',
    'shadow-cta hover:shadow-cta-hover',
    'hover:scale-[1.02] active:scale-[0.98]'
  ),
  secondary: cn(
    'bg-bg-pill-secondary text-white border border-white/10',
    'bg-[image:var(--gradient-pill-secondary,radial-gradient(16.7%_42.2%_at_89%_0%,#2E559E_0%,transparent_100%))]',
    'hover:bg-white/5 hover:border-white/20'
  ),
  ghost: cn(
    'bg-transparent text-white/80 border border-white/10',
    'hover:text-white hover:bg-white/5'
  ),
};

const sizeStyles: Record<Size, string> = {
  md: 'h-11 px-5 text-sm',
  lg: 'h-[52px] px-7 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      children,
      className,
      variant = 'primary',
      size = 'lg',
      icon,
      iconPosition = 'left',
      showChevron,
      type = 'button',
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-pill',
          'font-heading font-semibold tracking-tight',
          'transition-all duration-300 ease-soft',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
          'disabled:opacity-60 disabled:pointer-events-none',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...rest}
      >
        {icon && iconPosition === 'left' && (
          <span className="flex-shrink-0">{icon}</span>
        )}
        <span>{children}</span>
        {icon && iconPosition === 'right' && (
          <span className="flex-shrink-0">{icon}</span>
        )}
        {showChevron && (
          <ChevronRight className="h-4 w-4 -mr-1 transition-transform group-hover:translate-x-0.5" />
        )}
      </button>
    );
  }
);
